import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useRoles } from "@/context/RolesContext";
import { X, Trash, Plus, Clock } from "@phosphor-icons/react";

export function ProjectMembersModal({ project, onClose, onChanged }) {
  const [allUsers, setAllUsers] = useState([]);
  const [selected, setSelected] = useState(new Set(project.members || []));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users").then(r => setAllUsers(r.data)).catch(() => {});
  }, []);

  const toggle = (uid) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(uid)) s.delete(uid); else s.add(uid);
      return s;
    });
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const current = new Set(project.members || []);
      const toAdd = [...selected].filter(id => !current.has(id));
      const toRemove = [...current].filter(id => !selected.has(id));
      if (toAdd.length) await api.post(`/projects/${project.id}/members`, { user_ids: toAdd });
      for (const uid of toRemove) await api.delete(`/projects/${project.id}/members/${uid}`);
      onChanged && onChanged();
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="members-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="eyebrow">Team</div>
            <h2 className="font-display text-xl font-bold">Project members</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-6">
          <div className="text-xs text-muted-foreground mb-2">{selected.size} selected of {allUsers.length}</div>
          <div className="border border-border max-h-96 overflow-y-auto">
            {allUsers.map(u => (
              <label key={u.id} className="flex items-center gap-3 px-3 py-2 border-b border-border last:border-b-0 hover:bg-muted cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={selected.has(u.id)}
                  onChange={() => toggle(u.id)}
                  data-testid={`member-toggle-${u.id}`}
                />
                <div className="w-7 h-7 bg-[#0A0A0A] text-white text-xs flex items-center justify-center font-bold">
                  {u.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                <span className="text-[10px] font-mono-mini uppercase text-muted-foreground">{u.role}</span>
              </label>
            ))}
          </div>
          {error && <div className="mt-3 text-xs text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
          <div className="flex gap-3 mt-6">
            <button onClick={onClose} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
            <button onClick={save} disabled={saving} className="flex-1 btn-primary disabled:opacity-50" data-testid="save-members">
              {saving ? "Saving…" : "Save members"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_ACCENT = "#6B7280";

export function ProjectLanesModal({ project, onClose, onSaved }) {
  const [lanes, setLanes] = useState(project.lanes ? [...project.lanes] : []);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const setLane = (i, patch) => setLanes(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const remove = (i) => setLanes(ls => ls.filter((_, idx) => idx !== i));
  const add = () => {
    const key = "lane_" + Math.random().toString(36).slice(2, 7);
    setLanes(ls => [...ls, { key, label: "New Lane", accent: DEFAULT_ACCENT, order: ls.length }]);
  };
  const move = (i, dir) => {
    setLanes(ls => {
      const arr = [...ls];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr.map((l, idx) => ({ ...l, order: idx }));
    });
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const clean = lanes.map((l, idx) => ({
        key: l.key.trim() || `lane_${idx}`,
        label: l.label.trim() || `Lane ${idx + 1}`,
        accent: l.accent || DEFAULT_ACCENT,
        order: idx,
      }));
      const keys = clean.map(l => l.key);
      if (new Set(keys).size !== keys.length) throw new Error("Lane keys must be unique");
      if (clean.length === 0) throw new Error("You need at least one lane");
      await api.put(`/projects/${project.id}/lanes`, { lanes: clean });
      onSaved && onSaved(clean);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lanes-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="eyebrow">Board</div>
            <h2 className="font-display text-xl font-bold">Customize lanes</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-3">
          <div className="text-xs text-muted-foreground">
            Rename, reorder, add or remove lanes. Existing tasks stay in whatever status they had —
            if you delete a lane its tasks stay orphaned so keep at least one matching lane.
          </div>
          {lanes.map((l, i) => (
            <div key={i} className="border border-border p-3 grid grid-cols-12 gap-2 items-center" data-testid={`lane-row-${i}`}>
              <div className="col-span-1 flex flex-col items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-xs font-mono-mini disabled:opacity-30">↑</button>
                <button onClick={() => move(i, 1)} disabled={i === lanes.length - 1} className="text-xs font-mono-mini disabled:opacity-30">↓</button>
              </div>
              <input
                value={l.label} onChange={(e) => setLane(i, { label: e.target.value })}
                className="col-span-4 border border-border px-2 py-1 text-sm"
                placeholder="Label"
                data-testid={`lane-label-${i}`}
              />
              <input
                value={l.key} onChange={(e) => setLane(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                className="col-span-3 border border-border px-2 py-1 text-xs font-mono-mini"
                placeholder="key"
                data-testid={`lane-key-${i}`}
              />
              <div className="col-span-3 flex items-center gap-2">
                <input
                  type="color" value={l.accent} onChange={(e) => setLane(i, { accent: e.target.value })}
                  className="w-8 h-8 border border-border cursor-pointer"
                />
                <div className="text-[10px] font-mono-mini text-muted-foreground">{l.accent}</div>
              </div>
              <button onClick={() => remove(i)} className="col-span-1 p-2 hover:text-[#FF3B30] justify-self-end" data-testid={`lane-delete-${i}`}>
                <Trash size={14} />
              </button>
            </div>
          ))}
          <button onClick={add} className="w-full border border-dashed border-border py-2 text-sm hover:bg-muted flex items-center justify-center gap-2" data-testid="lane-add">
            <Plus size={14} weight="bold" /> Add lane
          </button>
          {error && <div className="text-xs text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
            <button onClick={save} disabled={saving} className="flex-1 btn-primary disabled:opacity-50" data-testid="save-lanes">
              {saving ? "Saving…" : "Save lanes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProjectTimesheetModal({ project, onClose, onSaved }) {
  const { roles: allRoles } = useRoles();
  const [approver, setApprover] = useState(project.timesheet_approver_role || "");
  const [globalDefault, setGlobalDefault] = useState("manager");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/timesheets/config").then(r => setGlobalDefault(r.data.default_approver_role)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.patch(`/projects/${project.id}`, { timesheet_approver_role: approver || null });
      onSaved && onSaved(approver);
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const sortedRoles = [...allRoles].sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
  const effective = approver || globalDefault;
  const eff = allRoles.find(r => r.key === effective);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="ts-approver-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="eyebrow flex items-center gap-2"><Clock size={12} weight="bold" /> Timesheet</div>
            <h2 className="font-display text-xl font-bold">Approver</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            When a team member submits a timesheet that logs hours against <b>{project.name}</b>, the request is routed to users with the selected role.
          </p>
          <div>
            <label className="eyebrow block mb-2">Approver role</label>
            <div className="space-y-1">
              <label className={`flex items-center gap-3 border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted ${!approver ? "bg-muted" : ""}`}>
                <input
                  type="radio"
                  name="approver"
                  checked={!approver}
                  onChange={() => setApprover("")}
                  data-testid="ts-approver-default"
                />
                <div>
                  <div className="font-semibold">Use global default</div>
                  <div className="text-[10px] font-mono-mini text-muted-foreground">Currently: {globalDefault}</div>
                </div>
              </label>
              {sortedRoles.map(r => (
                <label key={r.key} className={`flex items-center gap-3 border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted ${approver === r.key ? "bg-muted" : ""}`}>
                  <input
                    type="radio"
                    name="approver"
                    checked={approver === r.key}
                    onChange={() => setApprover(r.key)}
                    data-testid={`ts-approver-${r.key}`}
                  />
                  <span
                    className="inline-block text-[10px] px-1.5 py-0.5 uppercase font-mono-mini text-white"
                    style={{ backgroundColor: r.color || "#0A0A0A" }}
                  >
                    {r.label}
                  </span>
                  {r.description && <span className="text-xs text-muted-foreground truncate">{r.description}</span>}
                </label>
              ))}
            </div>
          </div>
          {eff && (
            <div className="border border-border p-3 bg-muted">
              <div className="eyebrow mb-1">Effective approver role</div>
              <span className="inline-block text-[11px] px-2 py-0.5 uppercase font-mono-mini text-white" style={{ backgroundColor: eff.color || "#0A0A0A" }}>
                {eff.label}
              </span>
            </div>
          )}
          {error && <div className="text-xs text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
            <button onClick={save} disabled={saving} className="flex-1 btn-primary disabled:opacity-50" data-testid="save-ts-approver">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

